import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { Lookup } from '@shared/interfaces/lookup';
import { FormSubmittedService } from '@shared/services/form-submitted.service';

@Component({
    selector: 'sas-select-multiple-field-type',
    standalone: true,
    imports: [CommonModule, TranslateModule, FormsModule, ReactiveFormsModule],
    template: `
        <div class="mb-3 sas-select-multiple">
            <label [for]="field.id"
                   [id]="field.id + 'Label'"
                   class="form-label"
                   title="{{field.props['helptext'] | translate}}"
                   [textContent]="field.props.label"
                   translate="{{field.props.label}}">
            </label>
            @if(field.props.required){
            <span> *</span>
            }
            <div [title]="helpText" class="sas-inline-block">
                @if(helpText.length && !isReadonly){
                <i class="bi bi-info-circle-fill" style="font-size: 15px; cursor: help; margin-left: 5px;"></i>
                }
            </div>
            <select [name]="field.name"
                    [id]="field.id"
                    class="form-select sas-select-multiple"
                    [ngClass]="{'is-invalid' : field.formControl.invalid && (field.formControl.dirty || field.formControl.touched || formSubmittedService.isSubmitted())}"
                    title="{{field.props['helpText'] | translate}}"
                    multiple
                    size="10"
                    [formControl]="formControl"
                    required="{{field.props.required}}"
                    tabindex="0"
                    [attr.aria-labelledby]="field.id"
                    [attr.aria-disabled]="isReadonly"
                    [attr.aria-required]="field.props.required">
                @if (field.props['defaultText']) {
                <option [ngValue]="undefined">{{field.props['defaultText'] | translate}}</option>
                }
                @for (item of sasOptions; track $index) {
                <option [ngValue]="item.id">{{item.name}}</option>
                }
            </select>
            @if (field.formControl.invalid && (field.formControl.dirty && field.formControl.errors || formSubmittedService.isSubmitted())) {
            <div class="invalid-feedback" role="alert">
                @if (field.formControl.errors?.['required']) {
                <small class="ng-invalid text-danger"
                    translate="{{field.props['validation'].requiredText}}">
                </small>
                }
            </div>
            }
        </div>
    `,
    styleUrl: './sas-select-multiple-field-type.component.scss'
})
export class SasSelectMultipleFieldTypeComponent extends FieldType<FieldTypeConfig> {
    private http = inject(HttpClient);
    formSubmittedService = inject(FormSubmittedService);
    private translate = inject(TranslateService);

    helpText: string = '';
    sasOptions: Array<Lookup> = [];

    get isReadonly(): boolean {
        return (this.field.formControl.disabled || this.field.props.readonly) ?? false;
    }

    constructor() {
        super();
    }

    ngOnInit() {
        if (this.field.props['helptext']) {
            const helpText = this.translate.instant(this.field.props['helptext']);

            this.helpText = helpText === this.field.props['helptext'] ? '' : helpText;
        }

        if (this.field.props['dataSource']?.type === 'api') {
            this.getOptions(this.field.props['dataSource'].url)
                .subscribe((result: Array<Lookup>) => {
                    this.sasOptions = result;
                    this.formControl.setValue(this.field.model[`${this.field.key!}`]);
                });
        }
        else {
            this.sasOptions = [...this.field.props['sasOptions']];
        }

        this.markAsPristine();
    }

    ngDoCheck() {
        if (this.isReadonly && !this.formControl.disabled) {
            this.formControl.disable({ emitEvent: false });
        }

        if (!this.isReadonly && this.formControl.disabled) {
            this.formControl.enable({ emitEvent: false });
        }
    }

    private getOptions(urlFetch: string): Observable<Array<Lookup>> {
        return this.http.get<Array<Lookup>>(urlFetch);
    }

    private markAsPristine() {
        if (this.field.formControl) {
            this.field.formControl.markAsPristine();
            this.field.formControl.markAsUntouched();
        }
    }
}
